import { SHOPIFY_GRAPHQL_API_ENDPOINT } from '../config'

export async function fetchProduct(id, fetchAll = false) {
  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query: `
          query GetProduct($id: ID!) {
            product(id: $id) {
              id
              title
              vendor
              handle
              featuredImage {
                url
                id
              }
              tags
              productType
              variants(first: 50) {
                nodes {
                  title
                  displayName
                  id
                  image {
                    id
                    originalSrc: url
                  }
                  price
                  compareAtPrice
                  product {
                    id
                  }
                  selectedOptions {
                    name
                    value
                  }
                }
              }
              images(first: 5) {
                nodes {
                  originalSrc: url
                  id
                }
              }
              options(first: 50) {
                id
                name
                values
              }
            }
          }
          `,
        variables: { id: 'gid://shopify/Product/' + id },
      }),
    })
    const { data } = await res.json()
    if (fetchAll) {
      return {
        error: null,
        data: {
          ...data.product,
          images: data.product.images.nodes,
          variants: data.product.variants.nodes,
        },
      }
    }
    return {
      error: null,
      data: {
        id: data.product.id.replace('gid://shopify/Product/', ''),
        images: data.product.featuredImage?.url
          ? [data.product.featuredImage?.url]
          : [],
        title: data.product.title,
        vendor: data.product.vendor,
        handle: data.product.handle,
      },
    }
  } catch (e) {
    return {
      error: e,
      data: null,
    }
  }
}

export async function fetchProducts(ids, fetchAll = false) {
  if (!ids || ids.length === 0) {
    return {
      error: null,
      data: [],
    }
  }

  const query = `
        query {
            products(first: ${Math.min(ids.length, 250)}, query: "${ids.map((id) => `id:${id}`).join(' OR ')}") {
                edges {
                    node {
                        id
                        title
                        vendor
                        handle
                        featuredImage {
                          url
                          id
                        }
                      }
                }
            }
        }
    `
  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
      }),
    })
    const { data } = await res.json()
    const products = data.products.edges.map(({ node }) => node)
    return {
      error: null,
      data: products.map(product => ({
        id: product.id.replace('gid://shopify/Product/', ''),
        images: product.featuredImage?.url
          ? [product.featuredImage?.url]
          : [],
        title: product.title,
        vendor: product.vendor,
        handle: product.handle,
      })),
    }
  } catch (e) {
    return {
      error: e,
      data: [],
    }
  }
}

export async function fetchCollection(collectionId) {
  const query = `
    query {
      collection(id: "gid://shopify/Collection/${collectionId}"){
        id
        title
        handle
        image {
          id
          url
        }
      }
    }`
  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
      }),
    })
    const { data } = await res.json()
    return {
      data: {
        id: data.collection.id.replace('gid://shopify/Collection/', ''),
        images: data.collection.image?.url ? [data.collection.image.url] : [],
        title: data.collection.title,
        handle: data.collection.handle,
      },
      error: null,
    }
  } catch (e) {
    return {
      error: e,
      data: null,
    }
  }
}

export async function getDiscounts() {
  const { data: count = 250 } = await getDiscountCount()
  const query = `
    query {
    codeDiscountNodes(first: ${count}) {
          nodes {
            id
            codeDiscount {
              ... on DiscountCodeBasic {
                title
                codes(first: 1){
                  nodes{
                    code
                  }
                }
                customerGets {
                  value {
                    ... on DiscountAmount {
                      amount {
                        amount
                      }
                    }
                    ... on DiscountPercentage {
                      percentage
                    }
                  }
                }
              }
            }
          }
    }
  }`
  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
      }),
    })
    const response = await res.json()
    const { data } = response
    return {
      data: (data.codeDiscountNodes.nodes ?? [])
        .map((d) => ({
          id: d.id,
          title: d.codeDiscount?.title ?? '',
          code: d.codeDiscount?.codes?.nodes[0]?.code ?? '',
          amount: Boolean(
            parseFloat(d.codeDiscount?.customerGets?.value?.amount?.amount),
          )
            ? parseFloat(d.codeDiscount?.customerGets?.value?.amount?.amount)
            : null,
          percentage: d.codeDiscount?.customerGets?.value?.percentage ?? null,
        }))
        .filter((d) => d.code.includes('GLOOD')),
      error: null,
    }
  } catch (e) {
    console.log('error while fetching discount nodes', e)
    return {
      error: e,
      data: null,
    }
  }
}

export async function createDiscount(payload) {
  const query = `
    mutation discountCodeBasicCreate($basicCodeDiscount: DiscountCodeBasicInput!) {
      discountCodeBasicCreate(basicCodeDiscount: $basicCodeDiscount) {
        codeDiscountNode {
          codeDiscount {
            ... on DiscountCodeBasic {
              title
              codes(first: 10) {
                nodes {
                  code
                }
              }
              startsAt
              endsAt
              customerSelection {
                ... on DiscountCustomerAll {
                  allCustomers
                }
              }
              customerGets {
                value {
                  ... on DiscountPercentage {
                    percentage
                  }
                }
                items {
                  ... on AllDiscountItems {
                    allItems
                  }
                }
              }
              appliesOncePerCustomer
            }
          }
        }
        userErrors {
          field
          code
          message
        }
      }
    }`
  const input = {
    title: payload.title,
    code: payload.code,
    startsAt: new Date(),
    endsAt: null,
    customerSelection: {
      all: true,
    },
    customerGets: {
      value: {},
      items: {
        all: true,
      },
    },
    appliesOncePerCustomer: true,
  }
  if (payload.type === 'percentage') {
    input.customerGets.value = {
      percentage: payload.value / 100,
    }
  } else {
    input.customerGets.value = {
      discountAmount: {
        amount: payload.value,
        appliesOnEachItem: false,
      },
    }
  }
  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          basicCodeDiscount: input,
        },
      }),
    })
    const response = await res.json()
    console.log('response from create discount', response)
    const { data } = response
    return {
      data: data.discountCodeBasicCreate.codeDiscountNode.codeDiscount,
      error: null,
    }
  } catch (e) {
    return {
      error: e,
      data: null,
    }
  }
}

export async function getDiscountCount() {
  const query = `
    query discountCodesCount {
      discountCodesCount {
        count
      }
    }`
  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
      }),
    })
    const data = await res.json()
    return {
      data: data?.discountCodesCount?.count,
      error: null,
    }
  } catch (e) {
    return {
      error: e,
      data: null,
    }
  }
}

export async function checkShopEligibleForBundles() {
  const query = `
    query {
      shop {
        features{
          bundles{
            eligibleForBundles
          }
        }
      }
    }
  `

  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
      }),
    })
    const data = await res.json()
    return {
      data: data?.data?.shop?.features?.bundles?.eligibleForBundles,
      error: null,
    }
  } catch (e) {
    return {
      error: e,
      data: null,
    }
  }
}

export async function fetchProductTypes() {
  const response = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      query: `
        query {
          shop {
            productTypes(first:250){
              edges{
                node
              }
            }
          }
        }
      `,
    }),
  })

  const { data } = await response.json()

  return data.shop.productTypes.edges.map(({ node }) => node)
}

export async function fetchPublications() {
  const response = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      query: `
        {
          publications(first: 250) {
            nodes {
              id
              name
            }
          }
        }
      `,
    }),
  })

  const { data } = await response.json()

  return data.publications.nodes.map(({ id, name }) => ({
    id: parseInt(id.replace('gid://shopify/Publication/', '')),
    name,
  }))
}

export async function fetchAutomaticDiscount(id) {
  const GRAPHQL_QUERY = `
    query discountDetails($discountId: ID!) {
          discountNode(id: $discountId) {
              id
              discount {
                ... on DiscountAutomaticApp {
                    status
                }
              }
          }
      }
  `
  const response = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      query: GRAPHQL_QUERY,
      variables: {
        discountId: 'gid://shopify/DiscountNode/' + id,
      },
    }),
  })

  const { data } = await response.json()

  return data.discountNode?.discount || null
}

export async function fetchAppDetails() {
  const query = `
      {
      app  {
          isPostPurchaseAppInUse
      }
    }`

  const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      query,
    }),
  })

  const data = await res.json()
  return data.data.app
}

export async function getRandomProduct() {
  const query = `
        query {
            products(first: 1) {
                edges {
                    node {
                      id
                    }
                }
            }
        }`

  const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
    method: 'POST',
    body: JSON.stringify({
      query,
    }),
  })

  const data = await res.json()

  return parseInt(
    data?.data?.products?.edges[0]?.node?.id.replace(
      'gid://shopify/Product/',
      '',
    ),
  )
}

export async function fetchCollectionIdByHandle(handle) {
  const query = `
    query collectionByHandle($handle: String!) {
      collectionByHandle(handle: $handle) {
        id
      }
    }
  `

  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: {
          handle,
        },
      }),
    })

    const data = await res.json()
    const id = data?.data?.collectionByHandle?.id
    return {
      error: null,
      data: id ? parseInt(id.replace('gid://shopify/Collection/', '')) : null,
    }
  } catch (e) {
    return {
      error: e,
      data: null,
    }
  }
}

export async function fetchProductTags(fetchAll = true) {
  try {
    const query = `
      query GetProductTags($first: Int) {
      productTags(first: $first) {
        edges {
          node
        }
    }
  }
    `

    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: { first: fetchAll ? 250 : 10 },
      }),
    })

    const { data, errors } = await res.json()

    if (errors) {
      throw new Error(errors.map((error) => error.message).join(', '))
    }

    if (!data || !data.productTags) {
      throw new Error('Error fetching product tags')
    }

    const tags = data.productTags.edges.map((edge) => edge.node)

    return {
      error: null,
      data: tags,
    }
  } catch (e) {
    return {
      error: e.message || e,
      data: null,
    }
  }
}

export async function fetchProductVendors(fetchAll = true) {
  try {
    const query = `
      query GetProductVendors($first: Int) {
      productVendors(first: $first) {
        edges {
          node
        }
    }
  }
    `

    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      body: JSON.stringify({
        query,
        variables: { first: fetchAll ? 250 : 10 },
      }),
    })

    const { data, errors } = await res.json()

    if (errors) {
      throw new Error(errors.map((error) => error.message).join(', '))
    }

    if (!data || !data.productVendors) {
      throw new Error('Error fetching product vendors')
    }

    const tags = data.productVendors.edges.map((edge) => edge.node)

    return {
      error: null,
      data: tags,
    }
  } catch (e) {
    return {
      error: e.message || e,
      data: null,
    }
  }
}

export async function getMainThemeId() {
  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          query getMainThemeId {
            themes(first: 1, roles: [MAIN]) {
              edges {
                node {
                  id
                }
              }
            }
          }
        `,
      }),
    })
    const { data, errors } = await res.json()
    if (errors) {
      return {
        error: errors[0].message,
        data: null,
      }
    }
    return {
      error: null,
      data: data.themes.edges[0]?.node?.id,
    }
  } catch (e) {
    return {
      error: e,
      data: null,
    }
  }
}

export async function getTemplateFile(themeId, filename) {
  try {
    const res = await fetch(SHOPIFY_GRAPHQL_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          query getTemplate($themeId: ID!, $filename: String!) {
            theme(id: $themeId) {
              files(filenames: [$filename]) {
                nodes {
                  filename
                  body {
                    ... on OnlineStoreThemeFileBodyText { content }
                    ... on OnlineStoreThemeFileBodyBase64 { contentBase64 }
                  }
                }
              }
            }
          }
        `,
        variables: {
          themeId: themeId,
          filename: filename,
        },
      }),
    })
    const { data, errors } = await res.json()
    if (errors) {
      return {
        error: errors[0].message,
        data: null,
      }
    }
    return {
      error: null,
      data: data.theme.files.nodes[0],
    }
  } catch (e) {
    return {
      error: e,
      data: null,
    }
  }
}

export async function checkAppBlockPlacement(sectionId, pageType) {
  try {
    // Get the main theme ID
    const themeResult = await getMainThemeId()
    if (themeResult.error) {
      return {
        error: themeResult.error,
        data: false,
      }
    }

    const themeId = themeResult.data
    if (!themeId) {
      return {
        error: 'No main theme found',
        data: false,
      }
    }

    // Map page types to template names
    let templateName = 'index' // default to index
    if (pageType === 'product') {
      templateName = 'product'
    } else if (pageType === 'collection') {
      templateName = 'collection'
    } else if (pageType === 'cart') {
      templateName = 'cart'
    }

    // Get the template file - try different possible paths
    let templateFile = null
    let templateResult = null

    // Try templates directory first
    templateResult = await getTemplateFile(
      themeId,
      `templates/${templateName}.json`,
    )
    if (!templateResult.error && templateResult.data) {
      templateFile = templateResult.data
    }

    // If not found, try sections directory
    if (!templateFile) {
      templateResult = await getTemplateFile(
        themeId,
        `sections/${templateName}.json`,
      )
      if (!templateResult.error && templateResult.data) {
        templateFile = templateResult.data
      }
    }

    // If still not found, try just the filename
    if (!templateFile) {
      templateResult = await getTemplateFile(themeId, `${templateName}.json`)
      if (!templateResult.error && templateResult.data) {
        templateFile = templateResult.data
      }
    }

    if (!templateFile) {
      return {
        error: `Template ${templateName}.json not found in any location`,
        data: false,
      }
    }

    // Get the template content
    let templateContent = null
    if (templateFile.body.content) {
      templateContent = templateFile.body.content
    } else if (templateFile.body.contentBase64) {
      templateContent = atob(templateFile.body.contentBase64)
    } else {
      return {
        error: 'No template content found',
        data: false,
      }
    }
    function buildRegex(blockTypePrefix, widgetId) {
      
      return new RegExp(
        `"type"\\s*:\\s*"` +
          blockTypePrefix +
          `[^"]*"[^]*?"widget-id"\\s*:\\s*"` +
          widgetId +
          `"`,
        's'
      );
    }

    const blockTypePrefix = 'shopify://apps/glood-product-recommendations'
    return {
      error: null,
      data: buildRegex(blockTypePrefix, sectionId).test(templateContent),
    }
  } catch (e) {
    return {
      error: e.message,
      data: false,
    }
  }
}
